import React, { useContext, useEffect, useState } from "react";
import { Link, Routes, Route, useLocation, useNavigate } from "react-router-dom";
import classNames from "classnames";
import { useGetSale } from "../../hooks/useSale";
import { useGetInventory } from "../../hooks/useInventory";
import { useGetLists } from "../../hooks/useList";
import Inventory from "./Inventory";
import InventoryAdd from "./InventoryAdd";
import InventoryPrint from "./InventoryPrint";
import Profile from "./Profile";
import { AuthContext, EntitiesContext, UserContext } from "../../App";
import Loader from "../Loader";
import Dates from "./Dates";
import Faqs from "./Faqs";
import UserRecord from "../../records/user";
import DashboardHeader from "./DashboardHeader";

export default function Dashboard() {
  const location = useLocation();
  const navigate = useNavigate();
  const auth = useContext(AuthContext);
  const user = useContext(UserContext);
  const entities = useContext(EntitiesContext);
  const sale = entities.state.get("sale").find((s) => s.open);
  const items = sale
    ? entities.state.get("inventory").filter((i) => i.sale_id === sale.id)
    : entities.state.get("inventory").filter(() => false);

  const { isLoading } = useGetSale();
  const lists = useGetLists();
  const inventory = useGetInventory();

  const total = (inv) => {
    if (inv.count() < 1) return 0;
    if (inv.count() === 1) return inv.first().price;

    return inv.reduce((acc, cur) => acc + parseInt(cur.price), 0);
  };

  const donated = () => items.filter((i) => i.donate);
  const sold = () =>
    items.filter(
      (i) =>
        i.order(entities.state.get("order"))?.status === "paid" && !i.donate,
    );
  const donatedSold = () =>
    items.filter(
      (i) =>
        i.order(entities.state.get("order"))?.status === "paid" && i.donate,
    );

  const isDashboard = () => location.pathname === "/dashboard";

  const isLoginAsSession = window.localStorage.getItem("loginAsSession") === "true";

  const logout = () => {
    ["auth", "loginAsSession", "originalAdminAuth", "orderId"].forEach((key) =>
      window.localStorage.removeItem(key)
    );
    auth.setAuth(null);
    user.setUser(new UserRecord());
    navigate("/");
  };

  const returnToAdminLogin = () => {
    // Restore the original admin's auth
    const originalAuth = window.localStorage.getItem("originalAdminAuth");
    if (originalAuth) {
      const authData = JSON.parse(originalAuth);
      window.localStorage.setItem("auth", originalAuth);
      window.localStorage.removeItem("originalAdminAuth");
      window.localStorage.removeItem("loginAsSession");

      // Restore auth state
      auth.setAuth(authData.data.data);
      user.setUser(new UserRecord({ ...authData.data.data.attributes, is_logged_in: true }));

      navigate("/admin/cashiers");
    } else {
      // Fallback if original auth wasn't stored
      window.localStorage.removeItem("auth");
      window.localStorage.removeItem("loginAsSession");
      auth.setAuth(null);
      navigate("/");
    }
  };

  if (isLoading || lists.isLoading || inventory.isLoading) return <Loader />;

  if (!sale) {
    return (
      <div className="container">
        <DashboardHeader onSignOut={logout} />

        <div className="notification is-info">
          <div className="content has-text-centered">
            <h3>No Active Sale</h3>
            <p>
              There is no active sale at this time. Please check back later or
              contact the PTA for more information about upcoming sales.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container">
      {isLoginAsSession && (
        <div className="notification is-warning" style={{ marginBottom: "1rem" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <span>
              <strong>Logged in as:</strong> {user.user.first_name} {user.user.last_name}
            </span>
            <button className="button is-small is-link" onClick={returnToAdminLogin}>
              Return to Admin Login
            </button>
          </div>
        </div>
      )}

      <DashboardHeader title="Waitsfield Ski & Skate Sale 2026" onSignOut={logout} />

      <div className="tabs is-centered">
        <ul>
          <li
            className={classNames({
              "is-active": location.pathname === "/dashboard",
            })}
          >
            {" "}
            <Link to="/dashboard">Dashboard</Link>
          </li>
          <li
            className={classNames({
              "is-active": location.pathname === "/dashboard/inventory/add",
            })}
          >
            <Link to="/dashboard/inventory/add">Add Inventory</Link>
          </li>
          <li
            className={classNames({
              "is-active": location.pathname === "/dashboard/profile",
            })}
          >
            <Link to="/dashboard/profile">Profile</Link>
          </li>
          <li
            className={classNames({
              "is-active": location.pathname === "/dashboard/dates",
            })}
          >
            <Link to="/dashboard/dates">Important Dates</Link>
          </li>
          <li
            className={classNames({
              "is-active": location.pathname === "/dashboard/faqs",
            })}
          >
            <Link to="/dashboard/faqs">FAQs</Link>
          </li>
        </ul>
      </div>

      {isDashboard() && (
        <nav className="level">
          <div className="level-item has-text-centered">
            <div>
              <p className="heading">Total Non Donated Items/Sold*</p>
              <p className="title">
                ${total(items.filter((i) => !i.donate))}/${total(sold())}
              </p>
              <small>
                * Please note, the PTA takes 20% of the sold amount.
              </small>
            </div>
          </div>
          <div className="level-item has-text-centered">
            <div>
              <p className="heading">Total Donated/Sold</p>
              <p className="title">
                ${total(donated())}/${total(donatedSold())}
              </p>
            </div>
          </div>
        </nav>
      )}

      {isDashboard() && <Inventory sale={sale} items={items} />}

      <Routes>
        <Route path="/inventory/add" element={<InventoryAdd sale={sale} />} />
        <Route path="/inventory/:id" element={<InventoryAdd sale={sale} />} />
        <Route path="/inventory/print" element={<InventoryPrint />} />
        <Route path="/profile" element={<Profile />} />
        <Route path="/dates" element={<Dates sale={sale} />} />
        <Route path="/faqs" element={<Faqs />} />
      </Routes>
    </div>
  );
}
